import { Redirect, usePathname } from "expo-router";
import type { ReactNode } from "react";
import { Platform } from "react-native";
import { useProSettled } from "~/components/LocalDataBanner";
import { useAuth } from "~/lib/auth";
import { backendConfigured, DEPLOYMENT_ENVIRONMENT } from "~/lib/constants";

/**
 * Browser tests cannot buy a subscription, so a local build (served from
 * localhost) can be started with EXPO_PUBLIC_E2E_SKIP_PAYWALL=1 to get past the
 * Pro requirement. It only skips the screen: data stays local, nothing is
 * granted, and it does nothing on any deployed host.
 */
const SKIP_PAYWALL =
  process.env.EXPO_PUBLIC_E2E_SKIP_PAYWALL === "1" &&
  DEPLOYMENT_ENVIRONMENT === "local";

/** Routes that must work without an account or Pro (sign-in, links from email). */
const PUBLIC_PATHS = ["/auth", "/auth/callback", "/reset-password"];

/**
 * DoughBro on the web has no local-only mode: a visitor signs in (or creates an
 * account) and holds Pro — a free trial counts — before the app opens. Native
 * platforms pass straight through. The sign-in and subscription screens are
 * the existing routes, so the gate only redirects to them.
 */
export function WebAccessGate({ children }: { children: ReactNode }) {
  const pathname = usePathname().replace(/\/+$/, "") || "/";
  const { user, ready, hasPro } = useAuth();
  const settled = useProSettled();

  if (Platform.OS !== "web" || !backendConfigured) return <>{children}</>;
  if (!ready) return null;
  // Signed in on the sign-in screen: move on to the app or the trial offer.
  const open = hasPro || SKIP_PAYWALL;
  if (user && pathname === "/auth") {
    if (!open && !settled) return null;
    return <Redirect href={open ? "/" : "/subscription"} />;
  }
  if (PUBLIC_PATHS.includes(pathname)) return <>{children}</>;
  if (!user) return <Redirect href="/auth" />;
  if (!open && !settled) return null;
  if (!open && pathname !== "/subscription") {
    return <Redirect href="/subscription" />;
  }
  return <>{children}</>;
}
